import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';

import { serverSignOut } from '@/utils/session/get-server-session';
import {
  SIGN_OUT_FALLBACK,
  validateSignOutRedirectTo,
} from '@/utils/validate-sign-out-redirect';

/**
 * Signing out is the same act on both sides of the marketplace: destroy the
 * session, drop the client cache, go somewhere signed-out. The per-role routes
 * redirect here and keep working for existing links.
 *
 * The optional `to` param lets a flow choose where you land — "sign up with a
 * different email" wants the signup page, not login. It is validated against
 * open redirects, and against pointing back at a sign-out route, which would
 * loop.
 */
export const Route = createFileRoute('/sign-out')({
  // Validated here rather than at use: an unchecked `to` would make this an
  // open redirect. Optional in the type so the many links that just want the
  // default do not have to spell it out.
  validateSearch: (search): { to?: string } => {
    const to = validateSignOutRedirectTo(search.to);
    return to === SIGN_OUT_FALLBACK ? {} : { to };
  },
  beforeLoad: async ({ context }) => {
    await serverSignOut();
    context.queryClient.clear();
  },
  component: SignOut,
});

function SignOut() {
  const { to } = Route.useSearch();
  const navigate = useNavigate();
  const destination = to ?? SIGN_OUT_FALLBACK;

  useEffect(() => {
    navigate({ to: destination, replace: true });
  }, [destination, navigate]);

  return null;
}
